import moment from 'moment-timezone';
import { convertToRoomRule } from 'actions/rooms/RoomsDataConverter';
import {
    Availability,
    AvailabilityHour,
    DaycareAvailabilityDate,
    DaycareAvailabilityDropOffSlot,
    DaycareAvailabilityPickUpSlot,
    DaycareAvailabilityRoom,
    GroomingAvailableDate,
    GroomingAvailableHour
} from 'types/Availability';
import {
    AvailabilityDtos,
    AvailabilityHourDto,
    DaycareAvailabilityDateDto,
    DaycareAvailabilityDropOffSlotDto,
    DaycareAvailabilityPickUpSlotDto,
    DaycareAvailabilityRoomDto,
    GroomingAvailableDateDto,
    GroomingAvailableHourDto
} from './AvailabilityDtos';

export const convertAvailabilityData = (
    availabilityDataDto: AvailabilityDtos[],
    timeZone: string
): Availability[] => {
    return availabilityDataDto.map(item => ({
        availableHours: item.available_hours.map(hours => convertHours(hours, timeZone)),
        date: item.date,
        dayName: item.day_name
    }));
};

export const convertHours = (hours: AvailabilityHourDto, timeZone: string): AvailabilityHour => {
    return {
        datetime: moment(hours.datetime).tz(timeZone),
        staffAvailable: hours.staff_available,
        time: hours.time.toLowerCase()
    };
};

export const convertGroomingAvailabilityDate = (
    dto: GroomingAvailableDateDto,
    timeZone: string
): GroomingAvailableDate => {
    return {
        date: moment.tz(dto.date, 'YYYY-MM-DD', timeZone),
        dayName: dto.day_name,
        hours: dto.available_hours.map(hours => convertGroomingAvailabilityHour(hours, timeZone))
    };
};

export const convertGroomingAvailabilityHour = (
    dto: GroomingAvailableHourDto,
    timeZone: string
): GroomingAvailableHour => {
    return {
        time: dto.time,
        datetime: moment(dto.datetime).tz(timeZone),
        staffAvailable: dto.staff_available,
        staffUnavailable: dto.staff_unavailable
    };
};

export const convertDaycareAvailabilityDate = (
    dto: DaycareAvailabilityDateDto,
    timeZone: string
): DaycareAvailabilityDate => {
    return {
        date: moment.tz(dto.date, 'YYYY-MM-DD', timeZone),
        dayName: dto.day_name,
        reason: dto.reason,
        dropOffSlots: dto.drop_off_slots.map(slot =>
            convertDaycareAvailabilityDropOffSlot(slot, timeZone)
        )
    };
};

export const convertDaycareAvailabilityDropOffSlot = (
    dto: DaycareAvailabilityDropOffSlotDto,
    timeZone: string
): DaycareAvailabilityDropOffSlot => {
    return {
        time: dto.time,
        datetime: moment(dto.datetime).tz(timeZone),
        pickUpSlots: dto.pick_up_slots.map(slot =>
            convertDaycareAvailabilityPickUpSlot(slot, timeZone)
        )
    };
};

export const convertDaycareAvailabilityPickUpSlot = (
    dto: DaycareAvailabilityPickUpSlotDto,
    timeZone: string
): DaycareAvailabilityPickUpSlot => {
    return {
        time: dto.time,
        datetime: moment(dto.datetime).tz(timeZone),
        rooms: dto.rooms.map(room => convertDaycareAvailabilityRoom(room))
    };
};

const parseSizeEligibility = (
    value: string | null | undefined
): import('types/Room').SizeEligibility => {
    if (value === null || value === undefined) {
        return null;
    }

    const validValues = ['empty', 'empty_match', 'active_match', 'mismatch', 'inconsistent'];

    if (validValues.includes(value)) {
        return value as import('types/Room').SizeEligibility;
    }

    return null;
};

export const convertDaycareAvailabilityRoom = (
    dto: DaycareAvailabilityRoomDto
): DaycareAvailabilityRoom => {
    return {
        id: dto.id,
        name: dto.name,
        petTypeList: dto.pet_type_list ?? [],
        restrictedDogBreedList: dto.restriction_dog_breed_list ?? [],
        restrictedCatBreedList: dto.restriction_cat_breed_list ?? [],
        restrictedPetTagList: dto.restriction_pet_tag_list ?? [],
        roomRules: dto.room_rules.map(rule => convertToRoomRule(rule)),
        roomUnits: dto.room_units.map(roomUnit => ({
            id: roomUnit.id,
            name: roomUnit.name,
            available: roomUnit.available,
            occupancy: roomUnit.occupancy,
            activeSizeRuleId: roomUnit.active_size_rule_id ?? null,
            sizeEligibility: parseSizeEligibility(roomUnit.size_eligibility)
        }))
    };
};
